<script setup lang="ts">
import { computed, defineAsyncComponent, provide, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { boardHistoryTimespanChanged } from "@/analytics/events/history";
import { trackEvent } from "@/analytics/track";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import DropdownTrigger from "@/components/ui/DropdownMenu/DropdownTrigger.vue";
import { menuListConfigKey } from "@/components/ui/MenuList/injectKeys";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import type { DateFilter, DateItem } from "@/store/activity";
import { useActivityStore } from "@/store/activity";
import { isNow } from "@/utils/date";
import { formatShortDate, formatTime } from "@/utils/dateFormat";

const DatePicker = defineAsyncComponent(() => import("./DatePicker.vue"));

const { t } = useI18n();

const model = defineModel<DateItem | undefined>();

const props = defineProps<{ values: DateFilter[] }>();

const dropdownMenuRef = useTemplateRef("dropdownMenu");

provide(menuListConfigKey, { selectable: true });

const customDate = ref<[Date, Date] | null>(null);
const customDateItem = computed<DateFilter | null>(() =>
  customDate.value
    ? {
        id: "custom" as DateFilter["id"],
        name: /*$t*/ "boardDiff.dateCustom",
        fromDate: () => customDate.value?.[0] || new Date(),
        toDate: () => customDate.value?.[1] || new Date(),
        show: () => false,
      }
    : null,
);

const selection = ref(
  (model.value?.id === "custom" && customDateItem.value) ||
    props.values.find((value) => value.id === model.value?.id) ||
    props.values[0],
);

watch(selection, (value) => {
  trackEvent(boardHistoryTimespanChanged(value.id, useActivityStore().tab));
});

function date(value: Pick<DateFilter, "fromDate" | "toDate">) {
  return {
    from: formatShortDate(value.fromDate()),
    to: isNow(value.toDate(), 1000 * 60 * 5) // show 'now' if the datetime is now±5 min
      ? t("boardDiff.now")
      : formatShortDate(value.toDate()),
  };
}

/**
 * Generate the dropdown label, in the format:
 * `${selection.name} (${date.from} ~-${date.to})`
 */
const triggerLabel = computed(() => {
  const fromDate = selection.value.fromDate();
  const toDate = selection.value.toDate();

  // Special case: if the dates are the same, show the times
  if (fromDate.toDateString() === toDate.toDateString()) {
    const args = {
      date: formatShortDate(fromDate),
      from: formatTime(fromDate),
      to: formatTime(toDate),
    };
    return t(selection.value.name, args);
  }

  return t(selection.value.name, date(selection.value));
});

select(selection.value);

function select(value: DateFilter) {
  model.value = { id: value.id, fromDate: value.fromDate(), toDate: value.toDate() };
}

/**
 * When the user finalizes their selection in the date picker, update the model
 */
function onDatesSelected() {
  if (customDate.value?.[0] && customDate.value?.[1] && customDateItem.value) {
    model.value = { id: "custom", fromDate: customDate.value[0], toDate: customDate.value[1] };
    selection.value = customDateItem.value;
    dropdownMenuRef.value?.close();
  }
}
</script>

<template>
  <div class="date-filter">
    <DropdownMenu ref="dropdownMenu" width="lg" class="date-filter-dropdown">
      <template #trigger="{ isOpen }">
        <DropdownTrigger
          :value="triggerLabel"
          :aria-expanded="isOpen"
          aria-haspopup="listbox"
          aria-controls="date-filter-list"
          :icon-after="null"
          icon="general/calendar"
        />
      </template>
      <SelectableList
        id="date-filter-list"
        v-model="selection"
        v-keyboard-nav
        :values
        @update:model-value="select"
      >
        <template #default="{ item }">
          <span>{{ $t(item.name) }}</span>
        </template>
        <template #after="{ item }">
          <span class="dates">{{ $t("boardDiff.dates", date(item)) }}</span>
        </template>
      </SelectableList>
      <DatePicker
        v-model="customDate"
        :selected="selection?.id === 'custom'"
        @update:model-value="onDatesSelected"
      />
    </DropdownMenu>
  </div>
</template>

<style lang="scss">
.date-filter {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  .dropdown-menu {
    width: 100%;

    .dropdown-trigger {
      width: 100%;
    }
  }
}

.date-filter-dropdown {
  overflow: visible !important;
}
</style>
